




















































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as utils from '@/utils';
import AccountUsernameComponent from '@/components/AccountUsernameComponent.vue';                       // @ is an alias to /src
import PortfolioComponent from '@/components/PortfolioComponent.vue';                               // @ is an alias to /src
import RealizedGainsComponent from '@/components/RealizedGainsComponent.vue';                       // @ is an alias to /src
import ActivityFeedComponent from '@/components/ActivityFeedComponent.vue';                       // @ is an alias to /src
import ListingsComponent from '@/components/ListingsComponent.vue';                       // @ is an alias to /src
import OffersComponent from '@/components/OffersComponent.vue';                       // @ is an alias to /src
import MyAuctionsComponent from '@/components/MyAuctionsComponent.vue';                       // @ is an alias to /src
import FeedbackComponent from '@/components/FeedbackComponent.vue';                       // @ is an alias to /src
import { Account } from '@/Account';
import * as utils_tracker from '@/utils_tracker';
import * as utils_topshot from '@/utils_topshot';

type modeType = 'portfolio' | 'listings' | 'offers' | 'sales' | 'feedback' | 'myauctions' | 'activities'

@Component({
  name: 'Account',
  components: { AccountUsernameComponent, PortfolioComponent, RealizedGainsComponent, ActivityFeedComponent, ListingsComponent, OffersComponent, MyAuctionsComponent, FeedbackComponent }
})

export default class AccountComponent extends Vue {
  // prop reactive member variables publically available to the html template
  @Prop()
  public startingFlowAddress: string

  @Prop({ default: 'portfolio' })
  public startingMode: modeType

  @Prop({ default: false })
  public isSignedInUser: boolean

  // public reactive member variables available to the html template
  public lastFetchedURL: string = null;
  public account: Account = null
  public flowAddress: string = null
  public mode: modeType = null

  // public functions available to the html template
  public get loading(): boolean { return this.$store.state.loading }
  public isDapperMode(): boolean { return utils_topshot.getDapperMode(); }

  // useful for after mounting
  public async setUsernameOrFlowAddress(usernameOrFlowAddress: string) {
    this.flowAddress = usernameOrFlowAddress
    await this.fetchData()
  }

  // private, non-reactive member variables

  @Watch('mode')
  private async modeChanged(): Promise<void> {
    if (this.$store.state.devmode) console.log(`Accounts.modeChanged() ${this.account} ${this.mode}`)

    if (!this.account) return       // this fixes the case where this is called before account has been established (like on refresh)

    this.addParamsToURL()

    if (this.mode === 'portfolio') await (this.$refs.PortfolioComponent as PortfolioComponent).setFlowAddress(this.account.flowAddress, false)
    if (this.mode === 'listings') await (this.$refs.ListingsComponent as ListingsComponent).setFlowAddress(this.account.flowAddress, false)
    if (this.mode === 'offers') await (this.$refs.OffersComponent as OffersComponent).setFlowAddress(this.account.flowAddress, false)
    if (this.mode === 'sales') await (this.$refs.RealizedGainsComponent as RealizedGainsComponent).setFlowAddress(this.account.flowAddress, false)
    if (this.mode === 'myauctions') await (this.$refs.MyAuctionsComponent as MyAuctionsComponent).setFlowAddress(this.account.flowAddress, false)
    if (this.mode === 'feedback') await (this.$refs.FeedbackComponent as FeedbackComponent).setFlowAddress(this.account.flowAddress, false)
    if (this.mode === 'activities') await (this.$refs.ActivityFeedComponent as ActivityFeedComponent).setFlowAddress(this.account.flowAddress, false)
  }

  @Watch('$route.path')
  private async pathChanged(): Promise<void> {
    if (this.$store.state.devmode) console.log(`Accounts.pathChanged() ${this.$route.path}`)
  }

  // private functions not available directly to HTML template
  private async mounted() {
    if (this.$store.state.devmode) console.log(`${this.$options.name} mounted()`)

    utils_tracker.page(this.$options.name)
    
    await this.pathChanged()

    // from props
    this.flowAddress = this.startingFlowAddress
    this.mode = this.startingMode

    // from url
    if (this.$route.query.address) this.flowAddress = this.$route.query.address as string
    if (this.$route.query.mode) this.mode = this.$route.query.mode as modeType

    if (this.flowAddress) await this.fetchData()
  }
  private async beforeDestroy() { 
    if (this.$store.state.devmode) console.log(`${this.$options.name} beforeDestroy()`)
  }

  // add mode and flowAddress as query params to current url path
  private addParamsToURL() {
    let extra: string = ''
    if (this.flowAddress) extra += `&address=${encodeURIComponent(this.flowAddress)}`
    if (this.mode) extra += `&mode=${encodeURIComponent(this.mode)}`
    extra = extra.replace('&', '?')     // replace only the first one
    this.$router.replace(`${this.$route.path}${extra}`).catch(() => {})
  }

  private async fetchData(): Promise<boolean> {
    // hack that if flowAddress !== startingFlowAddress, which can happen if a logged-in user shares his myaccount URL
    if (this.startingFlowAddress && this.startingFlowAddress !== this.flowAddress) {
      const url: string = `/community-tools/account?address=${this.flowAddress}&mode=${this.mode}`
      if (url) window.location.href = url 
      return false
    }

    this.addParamsToURL()

    // Mar 3, 2022: hack to prevent TheLockerRoom account lookup which chews up resources and memory
    if (this.flowAddress && (this.flowAddress.toLocaleLowerCase() === 'fa57101aa0d55954' || this.flowAddress.toLocaleLowerCase() === 'thelockerroom')) {
      utils.toast(this, 'Error', `TheLockerRoom account is too large to load.`)
      return false
    }

    this.$store.commit('setLoading', { loading: true });
    try {
      const url: string = utils.getURLprefix() + `/api/topshot/account/${this.flowAddress}`
      if (this.$store.state.devmode) console.log(`Calling ${url}`);
      const response: any = await utils.makeGetCall(url);


      // in case old version
      if (utils.isClientOld()) {
        if (this.$store.state.devmode) console.log("Calling this.$router.go(0)");
        this.$router.go(0);
        this.$store.commit('setLoading', { loading: false });
        return false
      }

      // error check
      if (response.data.error) {
        utils.toast(this, 'Error', response.data.error)
        this.$store.commit('setLoading', { loading: false });
        return false
      }

      // set account into component
      if (response.data.account) {
        this.account = new Account(response.data.account)

        this.flowAddress = this.account.flowAddress

        if (this.mode === 'portfolio') await (this.$refs.PortfolioComponent as PortfolioComponent).setFlowAddress(this.account.flowAddress, false)
        if (this.mode === 'listings') await (this.$refs.ListingsComponent as ListingsComponent).setFlowAddress(this.account.flowAddress, false)
        if (this.mode === 'offers') await (this.$refs.OffersComponent as OffersComponent).setFlowAddress(this.account.flowAddress, false)
        if (this.mode === 'sales') await (this.$refs.RealizedGainsComponent as RealizedGainsComponent).setFlowAddress(this.account.flowAddress, false)
        if (this.mode === 'myauctions') await (this.$refs.MyAuctionsComponent as MyAuctionsComponent).setFlowAddress(this.account.flowAddress, false)
        if (this.mode === 'feedback') await (this.$refs.FeedbackComponent as FeedbackComponent).setFlowAddress(this.account.flowAddress, false)
        if (this.mode === 'activities') await (this.$refs.ActivityFeedComponent as ActivityFeedComponent).setFlowAddress(this.account.flowAddress, false)

        // do again to put flow address vs username into url
        this.addParamsToURL()

      }

    } catch (error) {
      if (error.toString().includes('Network Error') || error.toString().includes('status code 502')) utils.toast(this, 'Error', 'Undergoing Maintenance'); else utils.toast(this, 'Error', 'Could not load url')
      if (this.$store.state.devmode) console.log(error);
      this.$store.commit("setLoading", { loading: false });
      return false
    }
    this.$store.commit("setLoading", { loading: false });
    return true
  }
}
